import { Button, Search } from '@ads/components-react';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { useTutorClassList } from '../../../hooks/useTutorClassList';
import type { TutorClassOption } from '../../../types/tutorAi';
import { LOOKUP_SEARCH_DEBOUNCE_MS, LOOKUP_TAKE } from '../../../utils/tutorAiLimits';
import { ClassTable } from '../../organisms/ClassTable';

export interface TutorClassListTemplateProps {
  /** Abre o detalhe da turma, levando o rótulo porque a API não resolve id → nome. */
  readonly onOpenClass: (item: TutorClassOption) => void;
}

/**
 * Template da listagem de turmas do tutor.
 *
 * Turma é o único recorte da tela: o backend cria uma turma por curso, então um filtro de
 * curso selecionaria exatamente o mesmo conjunto que a busca por turma já seleciona.
 */
export function TutorClassListTemplate({ onOpenClass }: TutorClassListTemplateProps) {
  const { t } = useTranslation('tutorAI');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    // Limpar o campo deve zerar a busca na hora, sem esperar o debounce.
    if (searchInput === '') {
      setSearch('');
      return;
    }

    const timer = setTimeout(() => setSearch(searchInput), LOOKUP_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    classes,
    isLoading,
    isError,
    isTruncated,
    refetch: refetchClasses,
  } = useTutorClassList({ search });

  return (
    <div className="flex flex-col gap-xs">
      <PageHeader title={t('classes.title')} subtitle={t('classes.subtitle')} />

      <div className="grid grid-cols-1 gap-mini md:grid-cols-2">
        <Search
          label={t('classes.filters.search')}
          placeholder={t('classes.filters.searchPlaceholder')}
          value={searchInput}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchInput(event.target.value)}
          size="md"
        />
      </div>

      {isError ? (
        <Alert type="danger">
          <div className="flex w-full flex-wrap items-center justify-between gap-mini">
            <span>{t('classes.loadError')}</span>
            <Button hierarchy="secondary" size="sm" onClick={() => void refetchClasses()}>
              {t('classes.retry')}
            </Button>
          </div>
        </Alert>
      ) : (
        <>
          {isTruncated ? (
            <Alert
              type="information"
              description={t('classes.truncated', { count: LOOKUP_TAKE })}
            />
          ) : null}

          <ClassTable classes={classes} isLoading={isLoading} onRowClick={onOpenClass} />
        </>
      )}
    </div>
  );
}
